import { useState, useEffect, useMemo, useRef } from 'react'
import { Loader, X } from 'lucide-react'
import { alarmApi } from '../lib/alarmApi'

const OPERATORS = ['Greater Than', 'Less Than', 'Greater Or Equal To', 'Less Or Equal To', 'Equal To', 'Not Equal To']
const OP_SYMBOL = {
  'Greater Than': '>', 'Less Than': '<', 'Greater Or Equal To': '>=',
  'Less Or Equal To': '<=', 'Equal To': '==', 'Not Equal To': '!=',
}
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
// ISO day-of-week numbering used by the backend (Mon=1 … Sun=7)
const DAY_TO_ISO = { Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6, Sunday: 7 }
// value = IANA name (sent to backend); label = friendly display
const TIMEZONES = [
  { value: 'America/Los_Angeles', label: '(UTC-08:00) America/Los_Angeles' },
  { value: 'America/Denver', label: '(UTC-07:00) America/Denver' },
  { value: 'America/Chicago', label: '(UTC-06:00) America/Chicago' },
  { value: 'America/New_York', label: '(UTC-05:00) America/New_York' },
  { value: 'UTC', label: '(UTC+00:00) UTC' },
  { value: 'Asia/Kolkata', label: '(UTC+05:30) Asia/Kolkata' },
]

const emptyCondition = () => ({ loggerId: '', parameterId: '', operator: 'Greater Than', value: '' })
const rand32 = () => Array.from({ length: 32 }, () => Math.floor(Math.random() * 36).toString(36)).join('')

// ── Reverse mappings, used to repopulate the form when editing ──
// tree symbol ("<", ">=" …) → human operator label
const SYMBOL_TO_OP = Object.fromEntries(Object.entries(OP_SYMBOL).map(([label, sym]) => [sym, label]))
const REL_OPS = new Set(['<', '<=', '>', '>=', '==', '!='])
const COND_OPS = new Set(['&', '|'])
// ISO day number (Mon=1 … Sun=7) → day name
const ISO_TO_DAY = { 1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday', 5: 'Friday', 6: 'Saturday', 7: 'Sunday' }

// seconds-since-midnight → "HH:MM" (or '' when unset)
const secondsToHms = (sec) => {
  if (sec === null || sec === undefined || sec === '') return ''
  const n = Number(sec)
  if (Number.isNaN(n)) return ''
  const h = Math.floor(n / 3600)
  const m = Math.floor((n % 3600) / 60)
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

// Parse a Lisp-style condition tree — "(>= $p5 10)" or "(& (>= $p1 10) (< $p2 0))" —
// back into a flat list of UI leaves [{ parameterId, operator, value }].
// This is the inverse of trie_op_tree.py (TrieOpTreeConverter) and lets the
// modal show the conditions that were saved with the alarm.
const parseTreeToLeaves = (treeString) => {
  if (!treeString || typeof treeString !== 'string') return []
  // 1) tokenize into parens and atoms
  const tokens = []
  let i = 0
  while (i < treeString.length) {
    const ch = treeString[i]
    if (ch === '(' || ch === ')') { tokens.push(ch); i++ }
    else if (/\s/.test(ch)) { i++ }
    else { let j = i; while (j < treeString.length && !/[\s()]/.test(treeString[j])) j++; tokens.push(treeString.slice(i, j)); i = j }
  }
  // 2) parse into a nested array
  let pos = 0
  const parse = () => {
    if (tokens[pos] !== '(') return tokens[pos++]
    pos++ // consume '('
    const list = []
    while (pos < tokens.length && tokens[pos] !== ')') list.push(parse())
    pos++ // consume ')'
    return list
  }
  const root = tokens.length ? parse() : null
  // 3) walk: condOp groups recurse, relOp nodes become leaves
  const leaves = []
  const walk = (node) => {
    if (!Array.isArray(node) || node.length === 0) return
    const head = node[0]
    if (COND_OPS.has(head)) { node.slice(1).forEach(walk); return }
    if (REL_OPS.has(head)) {
      const m = typeof node[1] === 'string' ? node[1].match(/^\$p(\d+)$/) : null
      if (m) leaves.push({ parameterId: m[1], operator: SYMBOL_TO_OP[head] || 'Greater Than', value: node[2] != null ? String(node[2]) : '' })
    }
  }
  walk(root)
  return leaves
}

/**
 * Full "Event Monitor Settings" modal — create/edit an alarm (event monitor).
 * Matches the XD: Base Settings (left) + Trigger Conditions / Trigger Action
 * (Email/SMS/Push/Advanced) / Reset Conditions / Reset Action (right).
 */
export function EventMonitorSettingsModal({ open, onClose, onSaved, projectId, sites = [], editingAlarm = null }) {
  // ── Base settings ──
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [timezone, setTimezone] = useState('America/New_York')
  const [restrictRange, setRestrictRange] = useState(false)
  const [activeDays, setActiveDays] = useState({ Monday: true, Tuesday: true, Wednesday: true, Thursday: true, Friday: true, Saturday: false, Sunday: false })
  const [activeFrom, setActiveFrom] = useState('')
  const [activeTo, setActiveTo] = useState('')
  const [multiTrigger, setMultiTrigger] = useState(false)
  const [enabled, setEnabled] = useState(true)

  // ── Trigger / reset ──
  const [triggerConditions, setTriggerConditions] = useState([emptyCondition()])
  const [triggerPeriodMin, setTriggerPeriodMin] = useState(10)
  const [resetConditions, setResetConditions] = useState([{ ...emptyCondition(), operator: 'Less Or Equal To' }])
  const [resetPeriodMin, setResetPeriodMin] = useState(10)

  // ── Actions ──
  const [actionTab, setActionTab] = useState('Email')
  const [message, setMessage] = useState('')
  const [emailRecipients, setEmailRecipients] = useState([]) // [{ to, notification }]
  const [smsRecipients, setSmsRecipients] = useState([])     // [{ name, phone, notification }]
  const [selectedEmail, setSelectedEmail] = useState(null)   // index of the row the toolbar acts on
  const [selectedSms, setSelectedSms] = useState(null)
  const emailRefs = useRef({})
  const smsRefs = useRef({})
  const [pushReceiveMessage, setPushReceiveMessage] = useState(false)
  const [pushReceiveParam, setPushReceiveParam] = useState(false)
  const [advLog, setAdvLog] = useState(5)
  const [advTransmit, setAdvTransmit] = useState(5)
  const [advTransmitEnabled, setAdvTransmitEnabled] = useState(false)
  const [advSendReport, setAdvSendReport] = useState(false)
  const [advTriggerEWC, setAdvTriggerEWC] = useState(false)

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [testing, setTesting] = useState(false)
  const [testMsg, setTestMsg] = useState(null) // { type:'success'|'error', text }

  // Loggers = devices; parameters resolved per device
  const loggers = useMemo(() => {
    const out = []
    sites.forEach(site => (site.devices || []).forEach(dev => {
      const params = (dev.sensors || []).flatMap(s => (s.parameters || []).map(p => ({ id: p.id, name: p.name, unit: p.unit })))
      out.push({ id: dev.id, name: dev.name, siteName: site.name, parameters: params })
    }))
    return out
  }, [sites])
  const paramsForLogger = (loggerId) => loggers.find(l => String(l.id) === String(loggerId))?.parameters || []

  // Reset / hydrate the form when (re)opened
  useEffect(() => {
    if (!open) return
    setError(null); setTestMsg(null); setActionTab('Email')
    setSelectedEmail(null); setSelectedSms(null)

    if (!editingAlarm) {
      // ── create: clean slate ──
      setName(''); setDescription(''); setEnabled(true); setMultiTrigger(false)
      setTimezone('America/New_York')
      setRestrictRange(false)
      setActiveDays({ Monday: true, Tuesday: true, Wednesday: true, Thursday: true, Friday: true, Saturday: false, Sunday: false })
      setActiveFrom(''); setActiveTo('')
      setTriggerConditions([emptyCondition()])
      setResetConditions([{ ...emptyCondition(), operator: 'Less Or Equal To' }])
      setTriggerPeriodMin(10); setResetPeriodMin(10)
      setEmailRecipients([]); setSmsRecipients([]); setMessage('')
      return
    }

    // ── edit: hydrate from the saved alarm ──
    setName(editingAlarm.name || '')
    setDescription(editingAlarm.description || '')
    setEnabled(!!editingAlarm.enabled)
    setMultiTrigger(!!editingAlarm.multi_trigger)
    setTimezone(editingAlarm.timezone || 'America/New_York')

    // Active days / time range
    const dayNums = (editingAlarm.active_days || '').split(',').map(s => s.trim()).filter(Boolean).map(Number)
    const dayMap = { Sunday: false, Monday: false, Tuesday: false, Wednesday: false, Thursday: false, Friday: false, Saturday: false }
    dayNums.forEach(n => { if (ISO_TO_DAY[n]) dayMap[ISO_TO_DAY[n]] = true })
    const fromHms = secondsToHms(editingAlarm.active_from)
    const toHms = secondsToHms(editingAlarm.active_to)
    const allDays = dayNums.length === 7
    // The alarm was "range restricted" if it is limited to specific days or a time window
    setRestrictRange((dayNums.length > 0 && !allDays) || !!fromHms || !!toHms)
    setActiveDays(allDays || dayNums.length === 0
      ? { Sunday: true, Monday: true, Tuesday: true, Wednesday: true, Thursday: true, Friday: true, Saturday: true }
      : dayMap)
    setActiveFrom(fromHms); setActiveTo(toHms)

    // Trigger / reset windows (seconds → minutes)
    setTriggerPeriodMin(Math.round((editingAlarm.alarm_trigger_interval || 0) / 60))
    setResetPeriodMin(Math.round((editingAlarm.alarm_reset_interval || 0) / 60))

    // Conditions: parse the stored trees back into rows, resolving each param's logger
    const resolveLogger = (paramId) => {
      const lg = loggers.find(l => (l.parameters || []).some(p => String(p.id) === String(paramId)))
      return lg ? String(lg.id) : ''
    }
    const toRows = (treeString, fallbackOp) => {
      const leaves = parseTreeToLeaves(treeString)
      if (leaves.length === 0) return [{ ...emptyCondition(), operator: fallbackOp }]
      return leaves.map(l => ({ loggerId: resolveLogger(l.parameterId), parameterId: l.parameterId, operator: l.operator, value: l.value }))
    }
    setTriggerConditions(toRows(editingAlarm.condition_tree, 'Greater Than'))
    setResetConditions(toRows(editingAlarm.reset_tree, 'Less Or Equal To'))

    // Recipients + message live in separate Alarmaction rows — fetch them
    setEmailRecipients([]); setSmsRecipients([]); setMessage('')
    let cancelled = false
    alarmApi.getAlarmActions(editingAlarm.id)
      .then(res => {
        if (cancelled) return
        const actions = Array.isArray(res.data) ? res.data : (res.data?.results || [])
        const action = actions.find(a => a.type === 'trigger') || actions[0]
        if (!action) return
        setMessage(action.message || '')
        const recipients = (action.sendto || '').split(/[\n,]+/).map(s => s.trim()).filter(Boolean)
        const notif = action.include_parameters ? 'Message, Parameter Value' : 'Message'
        if ((action.method || 'email').toLowerCase() === 'sms') {
          setActionTab('SMS')
          setSmsRecipients(recipients.map(phone => ({ name: '', phone, notification: notif })))
        } else {
          setActionTab('Email')
          setEmailRecipients(recipients.map(to => ({ to, notification: notif })))
        }
      })
      .catch(e => console.error('[EventMonitor] failed to load actions', e))
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingAlarm])

  if (!open) return null

  const updateCond = (list, setList, idx, field, value) =>
    setList(list.map((c, i) => i === idx ? { ...c, [field]: value } : c))

  // Build the legacy-compatible `conditions` object from the UI rows.
  // One valid rule → a leaf {relOp, values}; many → {condOp:'&', rules:[...]}.
  const buildConditions = (rows) => {
    const valid = rows.filter(c => c.parameterId && c.value !== '')
    const leaf = (c) => ({ relOp: OP_SYMBOL[c.operator], values: [`$p${c.parameterId}`, Number(c.value)] })
    if (valid.length === 0) return {}
    if (valid.length === 1) return leaf(valid[0])
    return { condOp: '&', rules: valid.map(leaf) }
  }

  const buildAction = () => {
    if (actionTab === 'Email' && emailRecipients.some(r => r.to)) {
      return {
        method: 'email',
        message: message || '',
        options: emailRecipients.some(r => r.notification === 'Message, Parameter Value') ? ['params'] : [],
        recipient: { type: 'individual', emails: emailRecipients.filter(r => r.to).map(r => r.to) },
      }
    }
    if (actionTab === 'SMS' && smsRecipients.some(r => r.phone)) {
      return {
        method: 'sms',
        message: message || '',
        options: smsRecipients.some(r => r.notification === 'Message, Parameter Value') ? ['params'] : [],
        recipient: { type: 'individual', emails: smsRecipients.filter(r => r.phone).map(r => r.phone) },
      }
    }
    return undefined
  }

  const handleSave = async () => {
    if (!name.trim()) { setError('Event Name is required.'); return }
    setSaving(true); setError(null)
    try {
      // ISO day numbers (Mon=1..Sun=7); when not restricting, send all 7
      const activeDayNums = restrictRange
        ? DAYS.filter(d => activeDays[d]).map(d => DAY_TO_ISO[d]).sort((a, b) => a - b)
        : [1, 2, 3, 4, 5, 6, 7]

      const triggerAction = buildAction()
      const payload = {
        project: projectId,
        name: name.trim(),
        description: description || '',
        type: 'alarm',
        enable: !!enabled,
        multiTrigger: !!multiTrigger,
        timezone,
        activeDays: activeDayNums,
        activeFrom: restrictRange ? (activeFrom || '') : '',
        activeTo: restrictRange ? (activeTo || '') : '',
        trigger: {
          alarmInterval: Math.max(0, Number(triggerPeriodMin) || 0) * 60,
          conditions: buildConditions(triggerConditions),
          ...(triggerAction ? { action: triggerAction } : {}),
        },
        reset: {
          alarmInterval: Math.max(0, Number(resetPeriodMin) || 0) * 60,
          conditions: buildConditions(resetConditions),
        },
      }

      if (editingAlarm?.id) {
        await alarmApi.updateEventMonitor(editingAlarm.id, payload)
      } else {
        await alarmApi.createEventMonitor(payload)
      }

      onSaved?.()
      onClose()
    } catch (e) {
      console.error('[EventMonitor] save failed', e)
      const msg = e.response?.data?.message || (e.response?.data ? JSON.stringify(e.response.data).slice(0, 300) : (e.message || 'Save failed'))
      setError(msg)
    } finally {
      setSaving(false)
    }
  }

  const handleTest = async () => {
    setTestMsg(null)
    const recipients = actionTab === 'Email'
      ? emailRecipients.filter(r => r.to).map(r => r.to)
      : actionTab === 'SMS'
        ? smsRecipients.filter(r => r.phone).map(r => r.phone)
        : []
    if (recipients.length === 0) {
      setTestMsg({ type: 'error', text: 'Add at least one recipient before testing.' })
      return
    }
    setTesting(true)
    try {
      const res = await alarmApi.testEventMonitor({
        method: actionTab === 'SMS' ? 'sms' : 'email',
        recipients,
        message: message || '',
      })
      setTestMsg({ type: 'success', text: res.data?.message || 'Test sent.' })
    } catch (e) {
      setTestMsg({ type: 'error', text: e.response?.data?.message || 'Test failed.' })
    } finally {
      setTesting(false)
    }
  }

  const removeCond = (list, setList, idx) => setList(list.filter((_, i) => i !== idx))

  // ── recipient toolbar (ADD / EDIT / REMOVE) — acts on the selected row,
  //    falling back to the last row, so they work on already-added recipients ──
  const addEmail = () => {
    setEmailRecipients(prev => { const next = [...prev, { to: '', notification: 'Message' }]; setSelectedEmail(next.length - 1); return next })
  }
  const editEmail = () => {
    const idx = selectedEmail != null ? selectedEmail : emailRecipients.length - 1
    if (idx < 0) return
    setSelectedEmail(idx)
    setTimeout(() => emailRefs.current[idx]?.focus(), 0)
  }
  const removeEmail = () => {
    const idx = selectedEmail != null ? selectedEmail : emailRecipients.length - 1
    if (idx < 0) return
    setEmailRecipients(prev => prev.filter((_, j) => j !== idx))
    setSelectedEmail(null)
  }
  const addSms = () => {
    setSmsRecipients(prev => { const next = [...prev, { name: '', phone: '', notification: 'Message' }]; setSelectedSms(next.length - 1); return next })
  }
  const editSms = () => {
    const idx = selectedSms != null ? selectedSms : smsRecipients.length - 1
    if (idx < 0) return
    setSelectedSms(idx)
    setTimeout(() => smsRefs.current[idx]?.focus(), 0)
  }
  const removeSms = () => {
    const idx = selectedSms != null ? selectedSms : smsRecipients.length - 1
    if (idx < 0) return
    setSmsRecipients(prev => prev.filter((_, j) => j !== idx))
    setSelectedSms(null)
  }

  // ── condition rows renderer (plain function — NOT a nested component, so
  //    inputs keep focus across re-renders). Shared by trigger + reset. ──
  const renderConditionRows = (list, setList, defaultOp) => (
    <>
      {list.map((c, idx) => (
        <div key={idx} className="flex items-start gap-2 mb-2">
          <div className="grid grid-cols-2 gap-2 flex-1">
            <select value={c.loggerId} onChange={e => updateCond(list, setList, idx, 'loggerId', e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 text-xs text-gray-600 focus:outline-none focus:border-[#358ECD]">
              <option value="">Please Choose A Logger</option>
              {loggers.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
            <select value={c.parameterId} onChange={e => updateCond(list, setList, idx, 'parameterId', e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 text-xs text-gray-600 focus:outline-none focus:border-[#358ECD]">
              <option value="">Please Choose A Parameter</option>
              {paramsForLogger(c.loggerId).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <select value={c.operator} onChange={e => updateCond(list, setList, idx, 'operator', e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 text-xs text-gray-600 focus:outline-none focus:border-[#358ECD]">
              {OPERATORS.map(o => <option key={o}>{o}</option>)}
            </select>
            <input type="number" placeholder="Number" value={c.value}
              onChange={e => updateCond(list, setList, idx, 'value', e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:border-[#358ECD]" />
          </div>
          {list.length > 1 && (
            <button type="button" title="Remove condition" onClick={() => removeCond(list, setList, idx)}
              className="mt-1 p-1 text-gray-400 hover:text-red-500 hover:bg-gray-100 rounded transition shrink-0">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      ))}
      <button type="button" onClick={() => setList([...list, { ...emptyCondition(), operator: defaultOp }])}
        className="text-xs font-medium text-[#358ECD] hover:underline">+ Add Condition</button>
    </>
  )

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-[#4D4D4D]">Event Monitor Settings</h2>
          <div className="flex items-center gap-2">
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-[#358ECD] text-white text-sm font-semibold rounded hover:bg-[#2d7ab8] transition disabled:opacity-60">
              {saving ? <Loader className="w-4 h-4 animate-spin" /> : null} Save Event
            </button>
            <button onClick={onClose} className="px-4 py-1.5 border border-gray-200 text-gray-600 text-sm rounded hover:bg-gray-50 transition">Cancel</button>
          </div>
        </div>

        {error && <div className="mx-6 mt-3 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded">{error}</div>}

        {/* Body */}
        <div className="flex-1 overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-8 px-6 py-5">
          {/* ── LEFT: Base Settings ── */}
          <div>
            <h3 className="text-sm font-semibold text-[#4D4D4D] mb-3">Base Settings</h3>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Event Name"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-3 focus:outline-none focus:border-[#358ECD]" />
            <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Description" rows={4}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-3 focus:outline-none focus:border-[#358ECD]" />
            <select value={timezone} onChange={e => setTimezone(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-4 focus:outline-none focus:border-[#358ECD]">
              {TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
            </select>

            <label className="flex items-center gap-2 text-xs text-gray-600 mb-3 cursor-pointer">
              <input type="checkbox" checked={restrictRange} onChange={e => setRestrictRange(e.target.checked)} className="w-4 h-4 accent-[#358ECD]" />
              Only send event notification in active date/time range.
            </label>

            <p className="text-xs font-medium text-gray-600 mb-2">Active Days</p>
            <div className="grid grid-cols-3 gap-y-2 gap-x-4 mb-4">
              {DAYS.map(d => (
                <label key={d} className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={!!activeDays[d]} onChange={e => setActiveDays(prev => ({ ...prev, [d]: e.target.checked }))} className="w-4 h-4 accent-[#358ECD]" />
                  {d}
                </label>
              ))}
            </div>

            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-medium text-gray-600 w-20">Active From</span>
              <input type="time" value={activeFrom} onChange={e => setActiveFrom(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:border-[#358ECD]" />
              <span className="text-[11px] text-gray-400">Leave Empty For All Day</span>
            </div>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xs font-medium text-gray-600 w-20">Active To</span>
              <input type="time" value={activeTo} onChange={e => setActiveTo(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:border-[#358ECD]" />
              <span className="text-[11px] text-gray-400">Leave Empty For All Day</span>
            </div>

            <label className="flex items-start gap-2 text-xs text-gray-600 mb-3 cursor-pointer">
              <span className="font-medium w-20 shrink-0">Multi-Trigger</span>
              <input type="checkbox" checked={multiTrigger} onChange={e => setMultiTrigger(e.target.checked)} className="w-4 h-4 accent-[#358ECD] mt-0.5" />
              <span>Check to send email as long as the event is triggered. Uncheck to send email once.</span>
            </label>
            <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
              <span className="font-medium w-20 shrink-0">Enable</span>
              <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} className="w-4 h-4 accent-[#358ECD]" />
              <span>Check to enable this event</span>
            </label>
          </div>

          {/* ── RIGHT: Conditions + Actions ── */}
          <div>
            {/* Trigger Conditions */}
            <h3 className="text-sm font-semibold text-[#4D4D4D] mb-1">Trigger Conditions</h3>
            <p className="text-xs text-gray-500 mb-2">Condition</p>
            {renderConditionRows(triggerConditions, setTriggerConditions, 'Greater Than')}
            <div className="flex items-center gap-2 mt-2 mb-5">
              <span className="text-xs text-gray-600">For All Readings In The Last</span>
              <input type="number" value={triggerPeriodMin} onChange={e => setTriggerPeriodMin(e.target.value)}
                className="w-20 border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:border-[#358ECD]" />
              <span className="text-xs text-gray-600">Min</span>
            </div>

            {/* Trigger Action */}
            <h3 className="text-sm font-semibold text-[#4D4D4D] mb-1">Trigger Action <span className="text-xs text-gray-400 font-normal">(Optional)</span></h3>
            <div className="flex gap-4 border-b border-gray-200 mb-3">
              {['Email', 'SMS', 'Push Notifications', 'Advanced'].map(t => (
                <button key={t} type="button" onClick={() => { setActionTab(t); setTestMsg(null) }}
                  className={`pb-2 text-sm font-medium border-b-2 transition ${actionTab === t ? 'border-[#358ECD] text-[#358ECD]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{t}</button>
              ))}
            </div>

            {testMsg && (
              <div className={`mb-3 px-3 py-2 rounded text-xs border ${testMsg.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
                {testMsg.text}
              </div>
            )}

            {actionTab === 'Email' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex gap-2">
                    <button type="button" onClick={addEmail}
                      className="px-3 py-1 border border-[#358ECD] text-[#358ECD] text-xs rounded hover:bg-[#358ECD]/10">ADD</button>
                    <button type="button" onClick={editEmail} disabled={emailRecipients.length === 0}
                      className="px-3 py-1 border border-[#358ECD] text-[#358ECD] text-xs rounded hover:bg-[#358ECD]/10 disabled:border-gray-200 disabled:text-gray-300 disabled:hover:bg-transparent">EDIT</button>
                    <button type="button" onClick={removeEmail} disabled={emailRecipients.length === 0}
                      className="px-3 py-1 border border-gray-300 text-gray-600 text-xs rounded hover:bg-gray-50 disabled:border-gray-200 disabled:text-gray-300 disabled:hover:bg-transparent">REMOVE</button>
                  </div>
                  <button type="button" onClick={handleTest} disabled={testing}
                    className="px-3 py-1 border border-[#358ECD] text-[#358ECD] text-xs rounded disabled:opacity-50">{testing ? 'Sending…' : 'TEST'}</button>
                </div>
                <div className="grid grid-cols-[1fr_1fr_24px] gap-2 text-[11px] font-semibold text-gray-500 px-1 mb-1"><span>Recipient</span><span>Notification</span><span></span></div>
                {emailRecipients.length === 0 && <p className="text-xs text-gray-400 px-1 py-2">No recipients. Click ADD.</p>}
                {emailRecipients.map((r, i) => (
                  <div key={i} onClick={() => setSelectedEmail(i)}
                    className={`grid grid-cols-[1fr_1fr_24px] gap-2 mb-1.5 items-center rounded px-1 -mx-1 ${selectedEmail === i ? 'ring-1 ring-[#358ECD] bg-[#358ECD]/5' : ''}`}>
                    <input ref={el => { emailRefs.current[i] = el }} value={r.to} placeholder="email@example.com" onChange={e => setEmailRecipients(prev => prev.map((x, j) => j === i ? { ...x, to: e.target.value } : x))}
                      className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:border-[#358ECD]" />
                    <select value={r.notification} onChange={e => setEmailRecipients(prev => prev.map((x, j) => j === i ? { ...x, notification: e.target.value } : x))}
                      className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:border-[#358ECD]">
                      <option>Message</option><option>Message, Parameter Value</option>
                    </select>
                    <button type="button" title="Remove recipient" onClick={(e) => { e.stopPropagation(); setEmailRecipients(prev => prev.filter((_, j) => j !== i)); setSelectedEmail(null) }}
                      className="p-1 text-gray-400 hover:text-red-500 rounded"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}

            {actionTab === 'SMS' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex gap-2">
                    <button type="button" onClick={addSms}
                      className="px-3 py-1 border border-[#358ECD] text-[#358ECD] text-xs rounded hover:bg-[#358ECD]/10">ADD</button>
                    <button type="button" onClick={editSms} disabled={smsRecipients.length === 0}
                      className="px-3 py-1 border border-[#358ECD] text-[#358ECD] text-xs rounded hover:bg-[#358ECD]/10 disabled:border-gray-200 disabled:text-gray-300 disabled:hover:bg-transparent">EDIT</button>
                    <button type="button" onClick={removeSms} disabled={smsRecipients.length === 0}
                      className="px-3 py-1 border border-gray-300 text-gray-600 text-xs rounded hover:bg-gray-50 disabled:border-gray-200 disabled:text-gray-300 disabled:hover:bg-transparent">REMOVE</button>
                  </div>
                  <button type="button" onClick={handleTest} disabled={testing}
                    className="px-3 py-1 border border-[#358ECD] text-[#358ECD] text-xs rounded disabled:opacity-50">{testing ? 'Sending…' : 'TEST'}</button>
                </div>
                <div className="grid grid-cols-[1fr_1fr_1fr_24px] gap-2 text-[11px] font-semibold text-gray-500 px-1 mb-1"><span>Recipient Name</span><span>Phone Number</span><span>Notification</span><span></span></div>
                {smsRecipients.length === 0 && <p className="text-xs text-gray-400 px-1 py-2">No recipients. Click ADD.</p>}
                {smsRecipients.map((r, i) => (
                  <div key={i} onClick={() => setSelectedSms(i)}
                    className={`grid grid-cols-[1fr_1fr_1fr_24px] gap-2 mb-1.5 items-center rounded px-1 -mx-1 ${selectedSms === i ? 'ring-1 ring-[#358ECD] bg-[#358ECD]/5' : ''}`}>
                    <input value={r.name} placeholder="Name" onChange={e => setSmsRecipients(prev => prev.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                      className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:border-[#358ECD]" />
                    <input ref={el => { smsRefs.current[i] = el }} value={r.phone} placeholder="Phone" onChange={e => setSmsRecipients(prev => prev.map((x, j) => j === i ? { ...x, phone: e.target.value } : x))}
                      className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:border-[#358ECD]" />
                    <select value={r.notification} onChange={e => setSmsRecipients(prev => prev.map((x, j) => j === i ? { ...x, notification: e.target.value } : x))}
                      className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:border-[#358ECD]">
                      <option>Message</option><option>Message, Parameter Value</option>
                    </select>
                    <button type="button" title="Remove recipient" onClick={(e) => { e.stopPropagation(); setSmsRecipients(prev => prev.filter((_, j) => j !== i)); setSelectedSms(null) }}
                      className="p-1 text-gray-400 hover:text-red-500 rounded"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}

            {actionTab === 'Push Notifications' && (
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={pushReceiveMessage} onChange={e => setPushReceiveMessage(e.target.checked)} className="w-4 h-4 accent-[#358ECD]" /> Receive Message
                </label>
                <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={pushReceiveParam} onChange={e => setPushReceiveParam(e.target.checked)} className="w-4 h-4 accent-[#358ECD]" /> Receive Parameter Value
                </label>
              </div>
            )}

            {actionTab === 'Advanced' && (
              <div className="space-y-3">
                <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={advTransmitEnabled} onChange={e => setAdvTransmitEnabled(e.target.checked)} className="w-4 h-4 accent-[#358ECD]" /> Triggered Transmit/Log Interval
                </label>
                {advTransmitEnabled && (
                  <div className="ml-6 space-y-2">
                    <div className="flex items-center gap-2"><span className="text-xs text-gray-600 w-16">Log (min)</span>
                      <input type="number" value={advLog} onChange={e => setAdvLog(e.target.value)} className="w-20 border border-gray-300 rounded px-2 py-1 text-xs" /></div>
                    <div className="flex items-center gap-2"><span className="text-xs text-gray-600 w-16">Transmit (min)</span>
                      <input type="number" value={advTransmit} onChange={e => setAdvTransmit(e.target.value)} className="w-20 border border-gray-300 rounded px-2 py-1 text-xs" /></div>
                  </div>
                )}
                <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={advSendReport} onChange={e => setAdvSendReport(e.target.checked)} className="w-4 h-4 accent-[#358ECD]" /> Send Report
                </label>
                <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={advTriggerEWC} onChange={e => setAdvTriggerEWC(e.target.checked)} className="w-4 h-4 accent-[#358ECD]" /> Trigger EWC
                </label>
              </div>
            )}

            {/* Message (shared) */}
            <p className="text-xs font-medium text-gray-600 mt-3 mb-1">Message</p>
            <input value={message} onChange={e => setMessage(e.target.value)} placeholder="Event message"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]" />
            <button type="button" className="text-xs font-medium text-[#358ECD] hover:underline mt-2">+ Add Action</button>

            {/* Reset Conditions */}
            <h3 className="text-sm font-semibold text-[#4D4D4D] mt-5 mb-1">Reset Conditions</h3>
            <p className="text-xs text-gray-500 mb-2">Condition</p>
            {renderConditionRows(resetConditions, setResetConditions, 'Less Or Equal To')}
            <div className="flex items-center gap-2 mt-2 mb-4">
              <span className="text-xs text-gray-600">For All Readings In The Last</span>
              <input type="number" value={resetPeriodMin} onChange={e => setResetPeriodMin(e.target.value)}
                className="w-20 border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:border-[#358ECD]" />
              <span className="text-xs text-gray-600">Min</span>
            </div>

            {/* Reset Action */}
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[#4D4D4D]">Reset Action <span className="text-xs text-gray-400 font-normal">(Optional)</span></h3>
              <button type="button" className="text-xs font-medium text-[#358ECD] hover:underline">+ Add Action</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
